import { memo, type Ref, useImperativeHandle, useRef } from "react";
import ProductEntry from "./ProductEntry";
import { type CartGrouping } from "../../../../models/CartData";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import {
  makeObserveWindowOffset,
  makeWindowScrollOffset,
} from "~/feature/cart/cart/components/CartPage/ProductTable/ProductEntryList/TanstackVirtualUtils";
import AlternateProducts from "../../AlternateProducts";
import { useAlternateProducts } from "~/feature/cart/cart/AlternateProductsContext";

export interface ProductEntryListRef {
  scrollToIndex: (index: number) => void;
}

interface ListProps {
  group: CartGrouping;
  editMode?: boolean;

  scrollRef?: Ref<ProductEntryListRef>;
}

export interface InsertedItem {
  index: number;
  code: string;
  designation: string;
}

const RegularProductEntryList = ({ group, editMode, scrollRef }: ListProps) => {
  const virtualListContainerRef = useRef<HTMLDivElement>(null);

  const { alternateItems, getEntryIndex, getInsertedItemAtIndex, handleInsert, handleClose } = useAlternateProducts();

  const rowVirtualizer = useWindowVirtualizer({
    count: group.entries.length + alternateItems.length, //count including insertedItems
    estimateSize: (virtualIndex) => {
      if (alternateItems.some((item) => item.index === virtualIndex)) {
        return 296; // height of AlternateProducts
      }
      const entryIndex = getEntryIndex(virtualIndex);
      return Math.max(460, 188 + group.entries[entryIndex].packGroups.flatMap((e) => e.packEntryIds).length * 245);
    },
    overscan: 8,
    // @ts-ignore
    observeElementOffset: makeObserveWindowOffset(virtualListContainerRef),
    // @ts-ignore
    scrollToFn: makeWindowScrollOffset(virtualListContainerRef),
    scrollPaddingEnd: 200,
    scrollPaddingStart: 200,
    shouldAdjustScrollPositionOnItemSizeChange: () => true,
  });

  useImperativeHandle(scrollRef, () => ({
    scrollToIndex: (index) => {
      rowVirtualizer.scrollToIndex(index);
      virtualListContainerRef.current?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({
        block: "center",
      });
    },
  }));

  return (
    <div
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
        width: "100%",
        position: "relative",
      }}
      ref={virtualListContainerRef}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const idx = virtualRow.index;
        const insertedItem = getInsertedItemAtIndex(idx);

        if (insertedItem) {
          return (
            <div
              key={idx}
              data-index={idx}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <AlternateProducts
                alternativesFor={insertedItem.designation}
                onClose={() => handleClose(idx)}
                orderEntryNumber={idx}
                alternateProductsId={insertedItem.code}
                onRender={() => {
                  const element = virtualListContainerRef.current?.querySelector(`[data-index="${idx}"]`);
                  if (element) {
                    rowVirtualizer.measureElement(element);
                  }
                }}
              />
            </div>
          );
        }

        const entryIndex = getEntryIndex(idx);

        return (
          <div
            key={idx}
            className={!editMode ? "scha__cart__product-entry--with-spacer" : ""}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualRow.start}px)`,
            }}
            data-index={idx}
          >
            <ProductEntry
              index={entryIndex}
              groupingEntry={group.entries[entryIndex]}
              editMode={editMode}
              onRender={() => {
                const element = virtualListContainerRef.current?.querySelector(`[data-index="${idx}"]`);
                if (element) {
                  rowVirtualizer.measureElement(element);
                }
              }}
              onDelete={(code, designation) => {
                handleInsert(idx, code, designation);
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

const ProductEntryList = RegularProductEntryList;

export default memo(ProductEntryList);
